import React, { useState, useEffect } from "react";
import { Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import SubscriptionService from "../../services/organization/SubscriptionService";

const QuotaBadge = ({ onExceededClick, className = "" }) => {
  const navigate = useNavigate();
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    let isMounted = true;
    SubscriptionService.getCurrentSubscription()
      .then((res) => {
        if (isMounted && res?.data?.data?.usage) {
          setQuota({
            ...res.data.data.usage,
            unlimited_documents: res.data.data.unlimited_documents,
            days_remaining: res.data.data.days_remaining
          });
        }
      })
      .catch((err) => {
        console.error("Failed to load quota badge:", err);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  if (!quota) return null;

  const used = quota.used_documents_count || 0;
  const limit = quota.document_limit || 0;
  const isUnlimited = quota.unlimited_documents;
  const isExceeded = quota.quota_exceeded;
  const remaining = Math.max(0, limit - used);
  const isNearLimit = !isUnlimited && !isExceeded && remaining <= 2;

  const getStatusColor = () => {
    if (isExceeded) return "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900";
    if (isNearLimit) return "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900";
    return "bg-zinc-50 text-zinc-700 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700";
  };

  const getDotColor = () => {
    if (isExceeded) return "#e11d48";
    if (isNearLimit) return "#d97706";
    return "#10b981";
  };

  const handleAction = (e) => {
    e.stopPropagation();
    if (onExceededClick) {
      onExceededClick(quota);
    } else {
      navigate("/billing");
    }
  };

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ${getStatusColor()} ${className}`}
    >
      <span className="w-2 h-2 rounded-full animate-pulse flex-shrink-0" style={{ backgroundColor: getDotColor() }} />
      <span>
        {isUnlimited ? "Unlimited Documents" : `${used}/${limit} Documents Used`}
      </span>
      {(isExceeded || isNearLimit) && (
        <button
          type="button"
          onClick={handleAction}
          className="ml-1 inline-flex items-center gap-1 font-bold underline hover:opacity-80 transition-opacity"
        >
          <Zap className="w-3 h-3 text-amber-600" />
          {isExceeded ? "Upgrade" : "Upgrade Plan"}
        </button>
      )}
    </div>
  );
};

export default QuotaBadge;
